import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ID } from '@gh/common-shared-data';
import { NotificationService, STYLES_DIALOG_LAYOUT, WaitingService } from '@gh/core-ui';
import { BulkReassignLeadsDto, LeadRestService } from '@gh/builder-shared-data';
import { LeadListBusinessService } from '../../resources/lead-list-business.service';

export type OfficeSalesPersonOption = {
  id: ID;
  name: string;
};

@Component({
  standalone: false,
  selector: 'gh-office-bulk-reassign-leads-dialog',
  templateUrl: 'office-bulk-reassign-leads-dialog.component.html',
  styleUrls: ['office-bulk-reassign-leads-dialog.component.scss'],
  styles: [STYLES_DIALOG_LAYOUT],
})
export class OfficeBulkReassignLeadsDialogComponent implements OnInit {
  leadIds: ID[] = [];
  officeId: ID;
  sourceSalespersonIds: string[] = [];
  salesPersons: OfficeSalesPersonOption[] = [];
  selectedSalesPersonIds: ID[] = [];
  useRoundRobin = false;
  private lastSalesPersonSelection: ID[] = [];

  get isSingleLeadReassign(): boolean {
    return this.leadIds.length === 1;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: any,
    private dialogRef: MatDialogRef<OfficeBulkReassignLeadsDialogComponent>,
    private leadRest: LeadRestService,
    private businessService: LeadListBusinessService,
    private notificationService: NotificationService,
    private waiting: WaitingService,
  ) {
    this.leadIds = data.leadIds || [];
    this.officeId = data.officeId;
    this.sourceSalespersonIds = data.sourceSalespersonIds || [];
  }

  ngOnInit(): void {
    this.loadSalesPersons();
  }

  private loadSalesPersons(): void {
    if (!this.officeId) {
      return;
    }
    this.businessService.findSalesPersonsByName({ from: 0, to: 1000 }, {
      officeIds: [this.officeId],
      showInactive: false,
    }).pipe(this.waiting.operator()).subscribe((res) => {
      this.salesPersons = res.filter((sp) => sp.id !== '');
      if (this.salesPersons.length === 1) {
        this.selectedSalesPersonIds = [this.salesPersons[0].id];
        this.lastSalesPersonSelection = [...this.selectedSalesPersonIds];
      }
    }, (err) => this.notificationService.processAll(err));
  }

  onSalesPersonIdsSelected(): void {
    if (this.isSingleLeadReassign && this.selectedSalesPersonIds.length > 1) {
      const newlySelected = this.selectedSalesPersonIds.find(
        (id) => !this.lastSalesPersonSelection.map(String).includes(String(id)),
      );
      this.selectedSalesPersonIds = newlySelected != null
        ? [newlySelected]
        : [this.selectedSalesPersonIds[this.selectedSalesPersonIds.length - 1]];
    }
    this.lastSalesPersonSelection = [...this.selectedSalesPersonIds];
  }

  isSaveDisabled(): boolean {
    if (this.selectedSalesPersonIds.length === 0) {
      return true;
    }
    return this.isSameSalesperson();
  }

  private isSameSalesperson(): boolean {
    if (this.sourceSalespersonIds.length === 0) {
      return false;
    }
    const targetIds = [...this.selectedSalesPersonIds].map(String).sort();
    const sourceIds = [...this.sourceSalespersonIds].map(String).sort();
    return targetIds.length === sourceIds.length &&
      targetIds.every((id, i) => id === sourceIds[i]);
  }

  onSave(): void {
    if (this.isSameSalesperson()) {
      this.notificationService.warning('msg_bulk_reassign_same_office_salesperson_error');
      return;
    }

    const request: BulkReassignLeadsDto = {
      leadIds: this.leadIds,
      targetOfficeId: this.officeId,
      targetSalespersonIds: this.selectedSalesPersonIds,
      useRoundRobin: this.isSingleLeadReassign ? false : this.useRoundRobin,
    };

    this.leadRest.bulkReassign(request)
      .pipe(this.waiting.operator())
      .subscribe((res: any) => {
        if (res && res.async) {
          this.notificationService.success('msg_bulk_reassign_leads_task_added');
        } else {
          this.notificationService.success('msg_bulk_reassign_leads_success');
        }
        this.dialogRef.close(true);
      }, (err) => {
        this.notificationService.processAll(err);
      });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
